import type {
    Characteristic,
    CombiCharacteristic,
    Component,
    ExpressionCharacteristic,
    MatrixValueCharacteristic,
    ProductAttributeCharacteristic,
} from '../schema';
import type { SelectionsMap } from '../state/selections';
import type { EvaluatedMap, EvaluatedValue } from './types';

const MAX_DEPTH = 16;
const seenProductAttributeWarnings = new Set<string>();

/**
 * Evaluate every calculated Characteristic in the Component in a single pass.
 * Returns a Map keyed by characteristic.id → resolved value (number / string /
 * null when unresolvable). Pure function — purely derived from
 * (Component, Selections).
 *
 * Cycle detection: characteristics currently mid-evaluation get tracked in a
 * `visiting` set; revisits short-circuit to `null` plus a one-shot
 * console.warn so the configurator stays responsive even if an admin
 * accidentally builds expression A → B → A.
 *
 * Depth protection: a chain longer than MAX_DEPTH levels also returns
 * `null` and warns. Real components rarely nest beyond 3-4 layers.
 */
/**
 * Map of Characteristic-id → externally-resolved value. Lets the bundle
 * inject values that the evaluator can't derive on its own:
 *
 *   - `webservice` — a fetch against the merchant's URL via the cloud
 *     proxy, run once at mount
 *   - `productattribute` — a host-shop product field (Shopify
 *     `product.weight`, Magento attribute, …) the host passes in
 *
 * Passing an unset id (or omitting the Map entirely) falls back to the
 * existing per-type evaluator branches, which return null with a
 * one-shot console.warn for these types.
 *
 * `hiddenCharacteristics` — ids a rule has removed. Those resolve to `null`
 * regardless of what the selections say, so dependent formulas see an
 * unfilled field. Pass it whenever a rule pass ran (see
 * `state/derived.ts:deriveConfiguration`); omit it only for isolated
 * evaluator calls with no rules in play.
 */
export type ExternalValues = ReadonlyMap<number, EvaluatedValue>;

export function evaluateAll(
    component: Component,
    selections: SelectionsMap,
    externalValues?: ExternalValues,
    hiddenCharacteristics?: ReadonlySet<number>,
): EvaluatedMap {
    const byAltTitle = new Map<string, Characteristic>();
    for (const group of component.groups) {
        for (const char of group.characteristics) {
            if (char.altTitle) byAltTitle.set(char.altTitle, char);
        }
    }

    const cache = new Map<number, EvaluatedValue>();
    const visiting = new Set<number>();
    const external = externalValues ?? EMPTY_EXTERNAL;
    const hidden = hiddenCharacteristics ?? EMPTY_HIDDEN;

    for (const group of component.groups) {
        for (const char of group.characteristics) {
            resolve(char, component, selections, byAltTitle, cache, visiting, external, hidden, 0);
        }
    }

    return cache;
}

const EMPTY_EXTERNAL: ExternalValues = new Map();
const EMPTY_HIDDEN: ReadonlySet<number> = new Set();

/**
 * Single-Characteristic lookup with the same cycle/depth protections.
 * Useful for the pricing pipeline which only needs one value at a time.
 */
export function evaluateCharacteristic(
    characteristic: Characteristic,
    component: Component,
    selections: SelectionsMap,
    externalValues?: ExternalValues,
    hiddenCharacteristics?: ReadonlySet<number>,
): EvaluatedValue {
    const all = evaluateAll(component, selections, externalValues, hiddenCharacteristics);
    return all.get(characteristic.id) ?? null;
}

function resolve(
    char: Characteristic,
    component: Component,
    selections: SelectionsMap,
    byAltTitle: Map<string, Characteristic>,
    cache: Map<number, EvaluatedValue>,
    visiting: Set<number>,
    external: ExternalValues,
    hidden: ReadonlySet<number>,
    depth: number,
): EvaluatedValue {
    if (cache.has(char.id)) return cache.get(char.id) ?? null;

    // Removed by a rule → no value anywhere, exactly as legacy's
    // `setValue(null)` on blacklist (option/abstract.js:1158-1160). The
    // scrubbed selections handle user-picked Characteristics; a calculated
    // one has no selection to scrub, so the exclusion has to happen here.
    // Dependent formulas then treat it like an unfilled field (`''` → 0)
    // and `calculatedLine` emits no price line.
    if (hidden.has(char.id)) {
        cache.set(char.id, null);
        return null;
    }

    if (visiting.has(char.id)) {
        warnOnce(`cycle.${char.altTitle || char.id}`, `cycle in expression chain at ${char.altTitle || char.id}`);
        cache.set(char.id, null);
        return null;
    }
    if (depth > MAX_DEPTH) {
        warnOnce(`depth.${char.altTitle || char.id}`, `expression chain too deep at ${char.altTitle || char.id}`);
        cache.set(char.id, null);
        return null;
    }

    // External-value short-circuit: webservice + productattribute defer
    // to the host. If the host provided a value for this id, use it
    // verbatim; otherwise fall through to the type-specific evaluator
    // (which usually returns null with a warn).
    if (char.type === 'webservice' || char.type === 'productattribute') {
        const provided = external.get(char.id);
        if (provided !== undefined) {
            cache.set(char.id, provided);
            return provided;
        }
    }

    visiting.add(char.id);
    let value: EvaluatedValue;
    switch (char.type) {
        case 'expression':
            value = evalExpression(char, component, selections, byAltTitle, cache, visiting, external, hidden, depth);
            break;
        case 'combi':
            value = evalCombi(char, component, selections, byAltTitle, cache, visiting, external, hidden, depth);
            break;
        case 'matrixvalue':
            value = evalMatrix(char, component, selections, byAltTitle, cache, visiting, external, hidden, depth);
            break;
        case 'productattribute':
            value = evalProductAttribute(char);
            break;
        default:
            value = resolveLeaf(char, selections);
            break;
    }
    visiting.delete(char.id);
    cache.set(char.id, value);
    return value;
}

// ── Per-type evaluators ─────────────────────────────────────────────────────

function evalExpression(
    char: ExpressionCharacteristic,
    component: Component,
    selections: SelectionsMap,
    byAltTitle: Map<string, Characteristic>,
    cache: Map<number, EvaluatedValue>,
    visiting: Set<number>,
    external: ExternalValues,
    hidden: ReadonlySet<number>,
    depth: number,
): EvaluatedValue {
    const rawExpr = char.expression?.trim();
    if (!rawExpr) return null;

    // Legacy syntax allows `$alt_title` and `$$alt_title` as placeholders
    // (configento/option/type/expression.js: var variables = expression.
    // match(/\${1,2}\w+[.]?[_]*\w*/g)). We accept both that AND bare
    // identifier syntax (`breite * hoehe / 10000`) — admins author either.
    // Stripping the $ prefixes normalises both forms into JS identifiers
    // so the rest of the pipeline (env binding + sandboxed Function) works
    // for both.
    // Admins frequently terminate expressions with a trailing `;` (legacy
    // `eval(myExpression)` accepts statement-style input). Our sandboxed
    // Function wraps the body as `return (<expr>)` — a trailing `;` inside
    // the parens is a SyntaxError and the whole formula returns null. Strip
    // them defensively. Mid-expression `;` would still fail; that's fine —
    // configurator expressions are one-liners by convention.

    // Suffix pass: `$altTitle.price`, `$altTitle.qty`, `$altTitle.value`,
    // `$altTitle.originalPrice` — legacy parity with
    // configento/option/type/expression.js:parseExpressionTitle. Legacy
    // routes to the active Choice's getPrice/getQty/getValue; V3 does the
    // same via resolveSuffix. Rewrite each hit to a synthetic identifier
    // (`__price__altTitle`) BEFORE strip so that a plain `foo.price` (JS
    // property access on a variable) remains untouched.
    const suffixed: Array<{ altTitle: string; suffix: Suffix }> = [];
    let expr = rawExpr.replace(
        /\${1,2}(\w+)\.(price|qty|value|originalPrice)\b/g,
        (_full, name: string, suffix: Suffix) => {
            suffixed.push({ altTitle: name, suffix });
            return synthName(suffix, name);
        },
    );
    expr = stripDollarPrefixes(expr).replace(/;\s*$/, '');

    const referenced = referencedAltTitles(expr);
    const env: Record<string, EvaluatedValue> = {};
    for (const ref of referenced) {
        // Skip our synthesised identifiers — they get bound explicitly
        // below with the suffix-specific value (price/qty/…).
        if (SYNTH_RE.test(ref)) continue;
        const dep = byAltTitle.get(ref);
        // Two "no value" cases collapse to the same handling, matching the
        // legacy storefront (configento/option/type/expression.js:156-158):
        //   - referenced alt_title doesn't exist on the component
        //   - exists but no selection has been made yet
        // Legacy substitutes the textual `$name` placeholder with `''`, so
        // `'' * 2` evaluates to `0` and the pricing pipeline shows 0 instead
        // of "—". We bind the Function arg to an empty string for the same
        // effect — `'' * X = 0` per JS numeric coercion, `'' + X` concatenates
        // naturally for string expressions.
        let depValue: EvaluatedValue = '';
        if (dep) {
            const resolved = resolve(dep, component, selections, byAltTitle, cache, visiting, external, hidden, depth + 1);
            if (resolved !== null) depValue = resolved;
        }
        env[sanitizeIdent(ref)] = depValue;
    }

    for (const { altTitle, suffix } of suffixed) {
        const dep = byAltTitle.get(altTitle);
        env[synthName(suffix, altTitle)] = resolveSuffix(dep, suffix, selections, hidden);
    }

    return runSandboxedExpression(expr, env, char.altTitle);
}

/**
 * Legacy `$altTitle.<field>` selectors — expression.js:parseExpressionTitle.
 * `value` and `originalPrice` are aliases we treat as `value` / `price`
 * respectively; V3 has no discount concept yet, so `originalPrice` is
 * price today. Kept as separate keys so the AI-generated formulas from
 * long-running legacy configurators keep parsing.
 */
type Suffix = 'price' | 'qty' | 'value' | 'originalPrice';

const SYNTH_RE = /^__(?:price|qty|value|originalPrice)__/;

function synthName(suffix: Suffix, altTitle: string): string {
    return `__${suffix}__${altTitle}`;
}

/**
 * Compute the value of `$altTitle.<suffix>` per legacy semantics.
 *
 * - `price` — active Choice's price (single/multi/quantities), or the
 *   Characteristic-level `price` field for free-entry / calculated types
 * - `qty`   — total quantity for multiquantity, else 1 (a single active
 *   pick counts as one unit — legacy expression.js:parseExpressionTitle
 *   returns `1` from Choice.getQty when no explicit quantity is set)
 * - `value` — same as bare `$altTitle` (delegates to resolveLeaf)
 * - `originalPrice` — same as `price` (V3 has no discount surface yet)
 *
 * Missing dep, missing selection, non-numeric price all collapse to `0`
 * so arithmetic keeps working. Legacy behaves the same — a `$foo.price`
 * against an unselected Characteristic contributes 0, not NaN.
 */
function resolveSuffix(
    char: Characteristic | undefined,
    suffix: Suffix,
    selections: SelectionsMap,
    hidden: ReadonlySet<number>,
): EvaluatedValue {
    if (!char) return 0;
    // Rule-removed Characteristic: no price, no units, no value — the same
    // answer a never-touched field gives. Checked here as well as in
    // `resolve` because the price/qty branches read the selections map
    // directly instead of going through the resolver.
    if (hidden.has(char.id)) return suffix === 'value' ? '' : 0;
    if (suffix === 'value') {
        return resolveLeaf(char, selections) ?? '';
    }

    const sel = selections[char.id];
    // price / originalPrice
    if (suffix !== 'qty') {
        if (!sel) return 0;
        if (sel.kind === 'single' && sel.valueId !== null && 'values' in char) {
            const picked = char.values.find((v) => v.id === sel.valueId);
            return picked ? (parseNumeric(picked.price ?? '') ?? 0) : 0;
        }
        if (sel.kind === 'multi' && 'values' in char) {
            let sum = 0;
            for (const id of sel.valueIds) {
                const picked = char.values.find((v) => v.id === id);
                if (picked) sum += parseNumeric(picked.price ?? '') ?? 0;
            }
            return sum;
        }
        if (sel.kind === 'quantities' && 'values' in char) {
            let sum = 0;
            for (const [idStr, qty] of Object.entries(sel.quantities)) {
                if (qty <= 0) continue;
                const picked = char.values.find((v) => v.id === Number(idStr));
                if (picked) sum += (parseNumeric(picked.price ?? '') ?? 0) * qty;
            }
            return sum;
        }
        // Free-entry / calculated: characteristic-level price is the
        // legacy "flat contribution when the field has a value" price.
        return parseNumeric(char.price ?? '') ?? 0;
    }

    // qty
    if (sel?.kind === 'quantities') {
        return Object.values(sel.quantities).reduce((a, b) => a + b, 0);
    }
    return 1;
}

/**
 * Normalise `$alt_title` / `$$alt_title` (legacy storefront syntax) into
 * bare identifiers. Numbers like `$10` would also strip — but that's fine:
 * `10` is a valid JS numeric literal and downstream parsing handles it.
 */
function stripDollarPrefixes(expr: string): string {
    return expr.replace(/\${1,2}(\w+)/g, '$1');
}

function evalCombi(
    char: CombiCharacteristic,
    component: Component,
    selections: SelectionsMap,
    byAltTitle: Map<string, Characteristic>,
    cache: Map<number, EvaluatedValue>,
    visiting: Set<number>,
    external: ExternalValues,
    hidden: ReadonlySet<number>,
    depth: number,
): EvaluatedValue {
    const fieldValues: EvaluatedValue[] = [];
    for (const rawRef of char.combiFields) {
        const ref = stripDollarPrefixes(rawRef);
        const dep = byAltTitle.get(ref);
        fieldValues.push(
            dep ? resolve(dep, component, selections, byAltTitle, cache, visiting, external, hidden, depth + 1) : null,
        );
    }

    const op = char.operatorValue ?? '+';
    if (op === 'string') {
        // Concatenate as labels — nulls become empty strings so the join
        // doesn't end up showing "undefined".
        return fieldValues.map((v) => (v == null ? '' : String(v))).join('');
    }

    const numeric = fieldValues.map((v) => (typeof v === 'number' ? v : Number(v)));
    if (numeric.some((n) => !Number.isFinite(n))) return null;

    switch (op) {
        case '+':
            return numeric.reduce((a, b) => a + b, 0);
        case '-':
            return numeric.reduce((a, b, i) => (i === 0 ? b : a - b), 0);
        case '*':
            return numeric.reduce((a, b) => a * b, 1);
        case '/':
            return numeric.reduce((a, b, i) => (i === 0 ? b : b === 0 ? NaN : a / b), 0);
        default:
            return null;
    }
}

function evalMatrix(
    char: MatrixValueCharacteristic,
    component: Component,
    selections: SelectionsMap,
    byAltTitle: Map<string, Characteristic>,
    cache: Map<number, EvaluatedValue>,
    visiting: Set<number>,
    external: ExternalValues,
    hidden: ReadonlySet<number>,
    depth: number,
): EvaluatedValue {
    const lookup = (ref: string) => lookupAlt(ref, byAltTitle, component, selections, cache, visiting, external, hidden, depth);

    const controlRef = char.controlMatrix ? stripDollarPrefixes(char.controlMatrix) : undefined;
    const table = selectMatrixTable(char, controlRef ? lookup(controlRef) : null);
    if (!table || table.data.length < 2) return null;

    // Dimensions can be strings (e.g. Material="holz" looked up against a
    // CSV header `material;holz;metall;…`) OR numbers (e.g. flaeche=2 looked
    // up against a Y-threshold column). Keep both shapes — pickMatrixIndex
    // handles equality per kind.
    const xVal = matrixAxisValue(char.matrixDimensionX, char.matrixFreeInputX, lookup);
    const yVal = matrixAxisValue(char.matrixDimensionY, char.matrixFreeInputY, lookup);
    if (xVal == null || yVal == null) return null;

    // Layout: row 0 = column-header (X values), col 0 = row-threshold (Y values).
    const headerRow = table.data[0] ?? [];
    const xOp = char.matrixOperatorX ?? 'match';
    const yOp = char.matrixOperatorY ?? 'match';

    const colIdx = pickMatrixIndex(xOp, xVal, headerRow.slice(1));
    if (colIdx === null) return null;
    const targetCol = colIdx + 1; // skip the leading row-threshold column

    const bodyRows = table.data.slice(1);
    const rowHeaders = bodyRows.map((row) => row[0]);
    const rowIdx = pickMatrixIndex(yOp, yVal, rowHeaders);
    if (rowIdx === null) return null;

    const cell = bodyRows[rowIdx]?.[targetCol];
    return toNumberMaybe(cell);
}

function evalProductAttribute(char: ProductAttributeCharacteristic): EvaluatedValue {
    // Resolution would require the embedding webshop's product context, which
    // the bundle doesn't have in the preview path. Surface a one-shot warning
    // and return null so the calculation chain doesn't silently turn into NaN.
    warnOnce(
        `productattribute.${char.altTitle || char.id}`,
        `productattribute "${char.productAttribute ?? ''}" not resolved — webshop product context missing`,
    );
    return null;
}

// ── Leaf resolution (selection / text / quantities) ────────────────────────

function resolveLeaf(char: Characteristic, selections: SelectionsMap): EvaluatedValue {
    const sel = selections[char.id];
    if (!sel) return null;

    switch (sel.kind) {
        case 'single': {
            if (sel.valueId === null) return null;
            // Legacy storefront `getValue()` semantics: the picked Value's
            // `value` field is what flows into expressions and matrix lookups
            // — internal IDs are configurator bookkeeping, not user-meaningful
            // identifiers. For a Material with values like {id: 7, value:
            // "holz"}, a matrix header row `material;holz;metall;…` only
            // matches when we return "holz", not 7.
            //
            // Numeric `.value` strings ("19.5", "100") parse to numbers so
            // arithmetic and matrix-Y "number lower" comparisons keep working.
            // Non-numeric codes come back as strings; matrix "match" handles
            // those via string compare.
            const picked = 'values' in char ? char.values.find((v) => v.id === sel.valueId) : undefined;
            if (picked && picked.value !== undefined && picked.value !== '') {
                const n = parseNumeric(String(picked.value));
                return n !== null ? n : picked.value;
            }
            // No `.value` set on the picked option (rare — admin didn't fill
            // it in). Fall back to the internal id so expressions don't break
            // entirely; matrix lookups against header strings will still miss,
            // but that's a configuration problem we can't paper over here.
            return Number(sel.valueId);
        }
        case 'multi':
            // Multi-pick → not directly usable in arithmetic. Sum of IDs is
            // meaningless; surface null and let the caller handle.
            return null;
        case 'text': {
            const trimmed = sel.value.trim();
            if (trimmed === '') return null;
            const n = parseNumeric(trimmed);
            return n !== null ? n : trimmed;
        }
        case 'quantities':
            return Object.values(sel.quantities).reduce((a, b) => a + b, 0);
        case 'files':
            // File-count is the only thing expressions can usefully reference
            // (e.g. "logo_files > 0 ? 5 : 0"). Returning null when empty so
            // numeric `null * X` propagates like other "no value" cases.
            return sel.files.length === 0 ? null : sel.files.length;
        case 'captcha':
            // Captcha proofs are opaque base64 — never useful in formulas.
            return null;
    }
}

// ── Helpers ────────────────────────────────────────────────────────────────

function lookupAlt(
    altTitle: string,
    byAltTitle: Map<string, Characteristic>,
    component: Component,
    selections: SelectionsMap,
    cache: Map<number, EvaluatedValue>,
    visiting: Set<number>,
    external: ExternalValues,
    hidden: ReadonlySet<number>,
    depth: number,
): EvaluatedValue {
    const dep = byAltTitle.get(altTitle);
    if (!dep) return null;
    return resolve(dep, component, selections, byAltTitle, cache, visiting, external, hidden, depth + 1);
}

function toNumberMaybe(v: unknown): number | null {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string') return parseNumeric(v);
    return null;
}

/**
 * Parse a numeric string with German-decimal-comma tolerance. Returns null
 * if the string isn't a number in either format.
 *
 * Mirrors legacy `expression.js:169` — the storefront has always accepted
 * "52,44" alongside "52.44" in admin-authored value fields. Without this
 * tolerance, components whose admins entered prices like "19,90" produce
 * NaN cascades in V3 (string-concat with NaN → "NaN52,4431,46" etc.) and
 * the formula UI shows garbage.
 */
function parseNumeric(raw: string): number | null {
    const trimmed = raw.trim();
    if (trimmed === '') return null;
    // "1.5" / "100" / "-3" — plain JS-native form.
    const direct = Number(trimmed);
    if (Number.isFinite(direct)) return direct;
    // "1,5" / "-19,90" — German decimal comma. Single comma only; thousand-
    // separator forms like "1.234,56" stay unsupported (legacy didn't handle
    // them either, and they'd be ambiguous against "1.234" = 1.234).
    if (/^-?\d+,\d+$/.test(trimmed)) {
        const swapped = Number(trimmed.replace(',', '.'));
        if (Number.isFinite(swapped)) return swapped;
    }
    return null;
}

/**
 * Pick the table a matrix looks up in. Legacy storefront
 * (option/type/matrix.js getCurrentMatrix): the control characteristic's
 * value is the 0-based position of the table in `matrix.values`; no control
 * value, or one that names no table, falls back to the first table.
 */
export function selectMatrixTable(
    char: MatrixValueCharacteristic,
    controlValue: EvaluatedValue,
): { name: string; data: unknown[][] } | undefined {
    const tables = char.matrix?.values ?? [];
    if (controlValue != null) {
        const key = String(controlValue).trim();
        if (/^\d+$/.test(key) && Number(key) < tables.length) return tables[Number(key)];
    }
    return tables[0];
}

/**
 * Resolve one matrix axis. `pc_free_input` means "a fixed value typed into
 * the matrix settings" (legacy app/storage.js); anything else is an
 * alt_title reference, which admins occasionally author with the `$` prefix
 * the expression engine uses — strip defensively so both forms resolve.
 */
function matrixAxisValue(
    dimension: string | undefined,
    freeInput: string | undefined,
    lookup: (ref: string) => EvaluatedValue,
): EvaluatedValue {
    if (!dimension) return null;
    if (dimension === 'pc_free_input') {
        const fixed = freeInput?.trim();
        return fixed ? fixed : null;
    }
    return lookup(stripDollarPrefixes(dimension));
}

export function pickMatrixIndex(
    op: string,
    value: number | string,
    candidates: ReadonlyArray<unknown>,
): number | null {
    if (op === 'match') {
        // String/number agnostic match. Both sides go through the same
        // normaliser: numeric strings compare as numbers ("19" === 19),
        // pure strings compare case-insensitively with surrounding
        // whitespace trimmed (admins authoring CSV by hand often leave
        // trailing spaces; the legacy storefront ignored them).
        const target = normaliseMatrixToken(value);
        for (let i = 0; i < candidates.length; i++) {
            if (normaliseMatrixToken(candidates[i]) === target) return i;
        }
        return null;
    }
    // Numeric semantics only — non-numeric values can't be ranked.
    const numericValue = toNumberMaybe(value);
    if (numericValue === null) return null;

    // "number higher" — smallest candidate ≥ value, i.e. round UP to the next
    // threshold. The legacy storefront returns the first row whose threshold
    // is `>= value` (option/type/matrix.js), the legacy admin labels the
    // operator "next largest number", and our own Matrix API documents it the
    // same way.
    //
    // This used to fall through to the branch below, so it rounded DOWN —
    // every in-between lookup landed one tier too low and produced a price
    // that differed from the legacy storefront without anything failing. It is
    // the most common operator in real components, which is the worst possible
    // combination: silent and widespread.
    //
    // Deliberate deviation from legacy: legacy scans in array order and takes
    // the first match, so an unsorted threshold list gives order-dependent
    // results. We take the smallest qualifying candidate, which is what the
    // operator means and what a sorted table — every table in practice —
    // yields anyway. The `number lower` branch below has always worked this
    // way too.
    if (op === 'number higher') {
        let bestIdx: number | null = null;
        let bestC = Infinity;
        for (let i = 0; i < candidates.length; i++) {
            const c = toNumberMaybe(candidates[i]);
            if (c === null) continue;
            if (c >= numericValue && c < bestC) {
                bestC = c;
                bestIdx = i;
            }
        }
        return bestIdx;
    }

    // "number lower" — largest candidate ≤ value, i.e. round DOWN. Also the
    // fallback for an unknown operator string, which is how the legacy
    // storefront behaved for anything it did not recognise... with one
    // exception: legacy clamps a below-range lookup to the first row, while we
    // return null and the matrix stays empty. Left as is on purpose — a
    // missing price is visible, a silently clamped one is not.
    let bestIdx: number | null = null;
    let bestC = -Infinity;
    for (let i = 0; i < candidates.length; i++) {
        const c = toNumberMaybe(candidates[i]);
        if (c === null) continue;
        if (c <= numericValue && c > bestC) {
            bestC = c;
            bestIdx = i;
        }
    }
    return bestIdx;
}

/**
 * Normalise a matrix-axis token for `op: 'match'`. Numeric inputs compare
 * by value (so "19" matches 19 and 19.0); string inputs compare by
 * lowercased trimmed text (so "Holz" matches " holz "). Mirrors the
 * loose-comparison behaviour the legacy storefront relied on.
 */
function normaliseMatrixToken(v: unknown): string {
    if (v == null) return '';
    if (typeof v === 'number') return Number.isFinite(v) ? String(v) : '';
    const s = String(v).trim();
    if (s === '') return '';
    const n = parseNumeric(s);
    if (n !== null) return String(n);
    return s.toLowerCase();
}

/**
 * Compile the expression with admin-authored JS in a sandboxed Function.
 * The expression is admin-trusted (same trust boundary as the rest of the
 * component JSON the cloud serves us), so `new Function` is acceptable.
 * Strict mode + no closure access keeps the sandbox honest.
 */
function runSandboxedExpression(
    expr: string,
    env: Record<string, EvaluatedValue>,
    altTitle: string,
): EvaluatedValue {
    // Merge admin-authored variables with the expression standard library.
    // Helpers go last so they win over any alt_title that happens to collide
    // — a merchant who names a Characteristic `round` cannot break the math
    // helper. That is the AI-documented contract (AiService.php: "round(...)")
    // and the same behaviour the legacy storefront's `eval()` gave via
    // whatever globals the host page defined.
    const merged: Record<string, unknown> = { ...env, ...EXPRESSION_HELPERS };
    const varNames = Object.keys(merged);
    const varValues = Object.values(merged);
    try {
        const fn = new Function(...varNames, `"use strict"; return (${expr});`);
        const result = (fn as (...args: unknown[]) => unknown)(...varValues);
        if (typeof result === 'number' && Number.isFinite(result)) return result;
        if (typeof result === 'string') return result;
        return null;
    } catch (err) {
        warnOnce(`expr.${altTitle || expr.slice(0, 32)}`, `expression eval failed at ${altTitle}: ${(err as Error).message}`);
        return null;
    }
}

/**
 * Standard library exposed to admin-authored expressions. Merchants (and the
 * AI code-generator, whose docs promise `round(...)` in AiService.php) reach
 * for bare math functions without a `Math.` prefix. Legacy configento uses
 * plain `eval()` and inherited whatever globals the host page defined; we
 * bind an explicit, minimal set here so `round`, `floor`, etc. work in the
 * sandbox regardless of the embedding page.
 *
 * `round` accepts an optional decimals arg (`round(1.234, 2) === 1.23`) —
 * the AI docs teach it that way and merchants author it that way. Native
 * `Math.round` only takes one arg, so a bare `Math.round` would silently
 * drop the decimals arg and always round to integer.
 */
const EXPRESSION_HELPERS: Readonly<Record<string, unknown>> = Object.freeze({
    round: (x: unknown, decimals?: unknown): number => {
        const n = typeof x === 'number' ? x : Number(x);
        if (!Number.isFinite(n)) return NaN;
        const d = typeof decimals === 'number' ? decimals : Number(decimals ?? 0);
        if (!Number.isFinite(d) || d <= 0) return Math.round(n);
        const factor = Math.pow(10, Math.floor(d));
        return Math.round(n * factor) / factor;
    },
    floor: Math.floor,
    ceil:  Math.ceil,
    abs:   Math.abs,
    min:   Math.min,
    max:   Math.max,
    sqrt:  Math.sqrt,
    pow:   Math.pow,
    log:   Math.log,
    exp:   Math.exp,
    PI:    Math.PI,
});

/**
 * Extract alt_titles referenced in an expression string. Legacy code used
 * literal identifiers (`breite`, `hoehe_oben`, …) — anything matching the JS
 * identifier shape is a candidate. JS built-ins (`Math`, `Number`, …) are
 * filtered out so we don't accidentally shadow them.
 */
function referencedAltTitles(expr: string): Set<string> {
    const out = new Set<string>();
    const idRe = /\b([a-zA-Z_$][\w$]*)\b/g;
    let m: RegExpExecArray | null;
    while ((m = idRe.exec(expr)) !== null) {
        const name = m[1];
        if (!name || JS_BUILTINS.has(name) || JS_KEYWORDS.has(name)) continue;
        if (name in EXPRESSION_HELPERS) continue;
        out.add(name);
    }
    return out;
}

const JS_BUILTINS = new Set([
    'Math', 'Number', 'String', 'Array', 'Object', 'Boolean', 'Date',
    'parseInt', 'parseFloat', 'isFinite', 'isNaN', 'NaN', 'Infinity',
    'undefined', 'null', 'true', 'false',
]);
const JS_KEYWORDS = new Set([
    'if', 'else', 'return', 'function', 'const', 'let', 'var', 'typeof',
    'instanceof', 'in', 'of', 'new', 'this', 'do', 'while', 'for', 'try',
    'catch', 'finally', 'throw', 'switch', 'case', 'break', 'continue',
    'default', 'void', 'delete', 'yield', 'await', 'async',
]);

/**
 * Some alt_titles aren't valid JS identifiers (e.g. start with a number).
 * Sanitise to `__altTitle__` so `new Function` doesn't choke. The expression
 * itself uses the original name — admins write `breite-1 * 2` (rare) — but
 * the variable scope still works because we never use the sanitised name
 * in the expression body.
 */
function sanitizeIdent(name: string): string {
    return /^[a-zA-Z_$]/.test(name) ? name : `_${name.replace(/[^\w$]/g, '_')}`;
}

function warnOnce(key: string, message: string): void {
    if (seenProductAttributeWarnings.has(key)) return;
    seenProductAttributeWarnings.add(key);
    console.warn(`[Configento] ${message}`);
}
